// =============================================================================
// AFRILAB LIMS - Location Configuration
// =============================================================================
// This configuration controls which features are visible based on location type
// =============================================================================

export type LocationType = 'MAIN_LAB' | 'SATELLITE' | 'STANDALONE';

export interface LocationConfig {
  type: LocationType;
  code: string;
  name: string;
  capabilities: string[];
}

// Get location config from environment variables
export const getLocationConfig = (): LocationConfig => {
  return {
    type: (import.meta.env.VITE_LOCATION_TYPE as LocationType) || 'MAIN_LAB',
    code: import.meta.env.VITE_LOCATION_CODE || 'MARRAKECH',
    name: import.meta.env.VITE_LOCATION_NAME || 'Laboratoire Marrakech',
    capabilities: (import.meta.env.VITE_CAPABILITIES || 'ALL').split(',').map((c: string) => c.trim()),
  };
};

// Check if current location is main lab
export const isMainLab = (): boolean => {
  return getLocationConfig().type === 'MAIN_LAB';
};

// Check if current location is satellite
export const isSatellite = (): boolean => {
  return getLocationConfig().type === 'SATELLITE';
};

// Check if current location is standalone (self-contained site with specific labs)
export const isStandalone = (): boolean => {
  return getLocationConfig().type === 'STANDALONE';
};

// Check if this site only has SAA lab (no ICP, AU, etc.) - used for simplified reception
export const isSAAOnlySite = (): boolean => {
  return hasCapability('LAB_SAA') && !hasCapability('LAB_ICP') && !hasCapability('LAB_AU') && !hasCapability('LAB_VOLUMETRIE');
};

// Check if location has a specific capability
export const hasCapability = (capability: string): boolean => {
  const config = getLocationConfig();
  return config.capabilities.includes('ALL') || config.capabilities.includes(capability);
};

// Location capabilities
export const LocationCapability = {
  RECEPTION: 'RECEPTION',
  PREP_MECANIQUE: 'PREP_MECANIQUE',
  LAB_ICP: 'LAB_ICP',
  LAB_SAA: 'LAB_SAA',
  LAB_AU: 'LAB_AU',
  LAB_VOLUMETRIE: 'LAB_VOLUMETRIE',
  VALIDATION: 'VALIDATION',
  CERTIFICATE: 'CERTIFICATE',
  TRANSFER_SEND: 'TRANSFER_SEND',
  TRANSFER_RECEIVE: 'TRANSFER_RECEIVE',
  ADMIN: 'ADMIN',
} as const;

// Dashboards visible for each location type
export const getDashboardsForLocationType = (type: LocationType): string[] => {
  if (type === 'SATELLITE') {
    return [
      'reception',
      'preparation-mecanique',
      'transfer-send',
      'sample-tracking',
    ];
  }

  if (type === 'STANDALONE') {
    const dashboards = ['reception', 'preparation-mecanique', 'admin'];
    if (hasCapability('LAB_SAA')) {
      dashboards.push('saa-prep-chimique', 'saa-analyst', 'saa-supervisor');
    }
    if (hasCapability('VALIDATION')) dashboards.push('validation');
    if (hasCapability('CERTIFICATE')) dashboards.push('certificates');
    return dashboards;
  }
  
  // MAIN_LAB sees all dashboards
  return [
    'reception',
    'preparation-mecanique',
    'transfer-receive',
    'icp-prep-chimique',
    'icp-analyst',
    'icp-supervisor',
    'saa-prep-chimique',
    'saa-analyst',
    'saa-supervisor',
    'au-prep-chimique',
    'au-analyst',
    'au-supervisor',
    'volumetrie-prep-chimique',
    'volumetrie-analyst',
    'volumetrie-supervisor',
    'validation',
    'certificates',
    'admin',
  ];
};

// Get visible dashboards for current location
export const getVisibleDashboards = (): string[] => {
  return getDashboardsForLocationType(getLocationConfig().type);
};

// Check if a specific dashboard should be visible
export const isDashboardVisible = (dashboard: string): boolean => {
  const config = getLocationConfig();
  
  // Main lab sees everything
  if (config.type === 'MAIN_LAB') return true;
  
  // Satellite and Standalone have limited dashboards based on capabilities
  const visibleDashboards = getVisibleDashboards();
  return visibleDashboards.includes(dashboard);
};

// Sample status for transfer
export const TRANSFER_STATUS = 'EN_COURS_ACHEMINEMENT';
